'use client'

import { useRef, useState, useEffect } from 'react'
import { Image } from '@vinted/web-ui'

import { getTestId } from '@marketplace-web/vendor-abstractions/web-ui-util'

import ImageCarouselIndicators from './ImageCarouselIndicators'

type Props = {
  images: Array<string>
  imageColor?: string | null
  accessibilityLabel?: string
  url?: string
  onClick?: (event: React.MouseEvent) => void
  openInNewTab?: boolean
  testId?: string
  renderDesktopFallback: () => React.ReactNode
}

const ImageCarousel = ({
  images,
  imageColor,
  accessibilityLabel,
  url,
  onClick,
  openInNewTab = false,
  testId,
  renderDesktopFallback,
}: Props) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)
  const [hasDragged, setHasDragged] = useState(false)

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return undefined

    const handleScroll = () => {
      const itemWidth = container.offsetWidth
      const index = Math.round(container.scrollLeft / itemWidth)

      setCurrentIndex(index)
    }

    container.addEventListener('scroll', handleScroll)

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleMouseDown = (event: React.MouseEvent) => {
    if (!scrollContainerRef.current) return
    setIsDragging(true)
    setHasDragged(false)
    setStartX(event.pageX - scrollContainerRef.current.offsetLeft)
    setScrollLeft(scrollContainerRef.current.scrollLeft)
  }

  const handleMouseLeave = () => {
    setIsDragging(false)
  }

  const handleMouseUp = () => {
    setIsDragging(false)
    // Defer reset to next tick so handleClick sees the drag state
    setTimeout(() => setHasDragged(false), 0)
  }

  const handleMouseMove = (event: React.MouseEvent) => {
    if (!isDragging || !scrollContainerRef.current) return
    event.preventDefault()
    const pageX = event.pageX - scrollContainerRef.current.offsetLeft
    const walk = (pageX - startX) * 3 // Higher multiplier = more sensitive
    if (Math.abs(walk) > 5) {
      setHasDragged(true)
    }
    scrollContainerRef.current.scrollLeft = scrollLeft - walk
  }

  const handleClick = (event: React.MouseEvent) => {
    if (hasDragged) {
      event.preventDefault()
      event.stopPropagation()

      return
    }

    if (onClick) {
      onClick(event)
    }

    if (url && !event.defaultPrevented) {
      if (openInNewTab) {
        window.open(url, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = url
      }
    }
  }

  return (
    <>
      <div className="new-item-box__carousel-container">
        <div
          role="presentation"
          ref={scrollContainerRef}
          className="new-item-box__carousel"
          data-testid={getTestId(testId, 'carousel')}
          onMouseDown={handleMouseDown}
          onMouseLeave={handleMouseLeave}
          onMouseUp={handleMouseUp}
          onMouseMove={handleMouseMove}
          onClick={handleClick}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className="new-item-box__carousel-item"
              onDragStart={event => event.preventDefault()}
            >
              <Image
                src={image}
                scaling="cover"
                ratio="portrait"
                color={imageColor}
                alt={accessibilityLabel}
                loading={index === 0 ? 'eager' : 'lazy'}
                testId={getTestId(testId, `carousel-image-${index}`)}
              />
            </div>
          ))}
        </div>
        {images.length > 1 && (
          <div
            className="new-item-box__carousel-indicators"
            data-testid={getTestId(testId, 'carousel-indicators')}
          >
            <ImageCarouselIndicators
              totalImages={images.length}
              currentIndex={currentIndex}
              testId={testId}
            />
          </div>
        )}
      </div>
      <div className="new-item-box__carousel-desktop-fallback">{renderDesktopFallback()}</div>
    </>
  )
}

export default ImageCarousel
